import React, { useRef, useEffect, useState } from 'react';
import { Maximize2, Minimize2, Play, Pause, RotateCcw, Volume2, VolumeX, SkipBack, SkipForward } from 'lucide-react';

interface VideoPreviewProps {
  videoUrl: string | null;
  loop?: boolean;
  onLoopChange?: (loop: boolean) => void;
  className?: string;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ 
  videoUrl, 
  loop = false, 
  onLoopChange,
  className = '' 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  // Verificar se há vídeo válido e forçar carregamento dos metadados
  useEffect(() => {
    if (videoUrl && videoUrl.trim() !== '') {
      setHasVideo(true);
      // Forçar carregamento dos metadados quando o vídeo mudar
      const video = videoRef.current;
      if (video) {
        // Resetar duração e tempo quando mudar o vídeo
        setDuration(0);
        setCurrentTime(0);
        // Pausar vídeo anterior antes de mudar
        if (!video.paused) {
          video.pause();
          setIsPlaying(false);
        }
        // Forçar reload para carregar metadados
        video.load();
      }
    } else {
      setHasVideo(false);
      setDuration(0);
      setCurrentTime(0);
      // Pausar vídeo se não houver URL
      const video = videoRef.current;
      if (video && !video.paused) {
        video.pause();
        setIsPlaying(false);
      }
    }
  }, [videoUrl]);

  // Atualizar loop do vídeo quando mudar
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.loop = loop;
    }
  }, [loop]);

  // Atualizar muted quando mudar
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Atualizar duração quando o vídeo carregar
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    const updateDuration = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
    };

    // Forçar carregamento dos metadados quando o vídeo mudar
    const loadVideo = () => {
      video.load();
    };

    // Event listeners para duração
    video.addEventListener('loadedmetadata', updateDuration);
    video.addEventListener('durationchange', updateDuration);
    video.addEventListener('loadeddata', updateDuration);
    video.addEventListener('canplay', updateDuration);

    // Forçar carregamento quando a URL mudar
    if (videoUrl) {
      // Pequeno delay para garantir que o src foi definido
      setTimeout(() => {
        if (video.readyState === 0) {
          video.load();
        }
      }, 100);
    }

    return () => {
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('durationchange', updateDuration);
      video.removeEventListener('loadeddata', updateDuration);
      video.removeEventListener('canplay', updateDuration);
    };
  }, [videoUrl]);

  // Atualizar tempo atual de forma fluida usando requestAnimationFrame
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;

    let isSeekingLocal = false;
    let rafId: number | null = null;

    // Função para atualizar o tempo usando requestAnimationFrame (mais fluido)
    const updateTime = () => {
      if (!isSeekingLocal && video && !video.paused && video.readyState >= 2) {
        const current = video.currentTime;
        if (isFinite(current)) {
          setCurrentTime(current);
        }
        rafId = requestAnimationFrame(updateTime);
      }
    };

    // Função para iniciar o loop de atualização
    const startUpdateLoop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (!video.paused && !isSeekingLocal && video.readyState >= 2) {
        rafId = requestAnimationFrame(updateTime);
      }
    };

    // Função para parar o loop de atualização
    const stopUpdateLoop = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // Atualizar tempo quando o vídeo estiver reproduzindo
    const handlePlay = () => {
      // Aguardar um pouco para garantir que o vídeo está pronto
      setTimeout(() => {
        startUpdateLoop();
      }, 50);
    };

    const handlePlaying = () => {
      startUpdateLoop();
    };

    const handlePause = () => {
      stopUpdateLoop();
      // Atualizar uma última vez quando pausar
      if (video && isFinite(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
    };

    // Usar timeupdate como fallback para garantir sincronização
    const handleTimeUpdate = () => {
      if (!isSeekingLocal && video && isFinite(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleSeeking = () => {
      isSeekingLocal = true;
      stopUpdateLoop();
    };

    const handleSeeked = () => {
      isSeekingLocal = false;
      if (video && !video.paused && isFinite(video.currentTime)) {
        setCurrentTime(video.currentTime);
        startUpdateLoop();
      }
    };

    // Quando os metadados carregarem, atualizar duração e tempo
    const handleLoadedMetadata = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
      if (isFinite(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleCanPlay = () => {
      if (video.duration && isFinite(video.duration)) {
        setDuration(video.duration);
      }
      // Se o vídeo já estiver reproduzindo, iniciar o loop
      if (!video.paused) {
        startUpdateLoop();
      }
    };

    // Event listeners
    video.addEventListener('play', handlePlay);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);

    // Iniciar loop se o vídeo já estiver reproduzindo e pronto
    if (!video.paused && video.readyState >= 2) {
      startUpdateLoop();
    }

    return () => {
      stopUpdateLoop();
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [videoUrl]);

  // Gerenciar fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    if (!containerRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Erro ao alternar fullscreen:', error);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    setIsMuted(!isMuted);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const newTime = parseFloat(e.target.value);
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const skipBackward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
  };

  const skipForward = () => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.min(videoRef.current.duration, videoRef.current.currentTime + 5);
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleEnded = () => setIsPlaying(false);

  if (!hasVideo) {
    return (
      <div className={`w-full border border-gray-200 rounded-lg shadow-sm overflow-hidden ${className}`}>
        <div className="bg-gray-50 p-3 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">Pré-visualização do Vídeo</h3>
        </div>
        <div className="bg-gray-100 p-12 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-gray-500 text-lg mb-2">Nenhum vídeo carregado</p>
            <p className="text-gray-400 text-sm">Faça upload de um vídeo .mp4 para visualizar</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`w-full border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-black ${className}`}
    >
      <div className="bg-gray-50 p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Pré-visualização do Vídeo</h3>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={loop}
              onChange={(e) => onLoopChange?.(e.target.checked)}
              className="rounded"
            />
            <span>Loop</span>
          </label>
          <button
            onClick={toggleFullscreen}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
            title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>
      
      <div 
        className="relative bg-black flex items-center justify-center group" 
        style={{ minHeight: '400px' }}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(true)}
      >
        <video
          key={videoUrl || 'no-video'} // Forçar remontagem quando vídeo mudar
          ref={videoRef}
          src={videoUrl || undefined}
          className="w-full h-auto max-h-[70vh]"
          loop={loop}
          muted={isMuted}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onLoadedMetadata={() => {
            const video = videoRef.current;
            if (video && video.duration && isFinite(video.duration)) {
              setDuration(video.duration);
            }
          }}
          onLoadedData={() => {
            const video = videoRef.current;
            if (video && video.duration && isFinite(video.duration)) {
              setDuration(video.duration);
            }
          }}
          onCanPlay={() => {
            const video = videoRef.current;
            if (video) {
              if (video.duration && isFinite(video.duration)) {
                setDuration(video.duration);
              }
              if (isFinite(video.currentTime)) {
                setCurrentTime(video.currentTime);
              }
            }
          }}
          onTimeUpdate={() => {
            const video = videoRef.current;
            if (video && isFinite(video.currentTime)) {
              setCurrentTime(video.currentTime);
            }
          }}
          onError={(e) => {
            const video = videoRef.current;
            const error = video?.error;
            console.error('❌ Erro ao carregar vídeo:', {
              error,
              errorCode: error?.code,
              errorMessage: error?.message,
              networkState: video?.networkState,
              readyState: video?.readyState,
              src: video?.src,
              currentSrc: video?.currentSrc,
              videoUrl: videoUrl
            });
            
            // Códigos de erro do HTMLVideoElement:
            // 1 = MEDIA_ERR_ABORTED - O usuário abortou o download
            // 2 = MEDIA_ERR_NETWORK - Ocorreu um erro de rede (vídeo não encontrado, CORS, etc)
            // 3 = MEDIA_ERR_DECODE - Ocorreu um erro ao decodificar o vídeo
            // 4 = MEDIA_ERR_SRC_NOT_SUPPORTED - O formato de vídeo não é suportado
            if (error) {
              let errorMsg = 'Erro desconhecido ao carregar vídeo';
              switch (error.code) {
                case 1:
                  errorMsg = 'Download do vídeo foi abortado';
                  break;
                case 2:
                  errorMsg = 'Erro de rede ao carregar vídeo. Verifique se o vídeo existe no servidor e se a URL está correta.';
                  break;
                case 3:
                  errorMsg = 'Erro ao decodificar o vídeo. O arquivo pode estar corrompido.';
                  break;
                case 4:
                  errorMsg = 'Formato de vídeo não suportado ou URL inválida.';
                  break;
              }
              console.error('📋 Detalhes do erro:', errorMsg);
              console.error('🔗 URL tentada:', videoUrl);
            }
          }}
          playsInline
          preload="metadata"
        />
        
        {/* Controles de reprodução completos */}
        {showControls && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            {/* Barra de progresso */}
            <div className="mb-3">
              <input
                type="range"
                min="0"
                max={duration || 0}
                step="0.1"
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer transition-all"
                style={{
                  background: duration > 0 
                    ? `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(currentTime / duration) * 100}%, #4b5563 ${(currentTime / duration) * 100}%, #4b5563 100%)`
                    : '#4b5563'
                }}
              />
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  appearance: none;
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  background: #3b82f6;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
                input[type="range"]::-moz-range-thumb {
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  background: #3b82f6;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                }
              `}</style>
            </div>

            {/* Controles principais */}
            <div className="flex items-center justify-between gap-4">
              {/* Controles da esquerda */}
              <div className="flex items-center gap-2">
                <button
                  onClick={togglePlay}
                  className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-white/10 rounded"
                  title={isPlaying ? 'Pausar' : 'Reproduzir'}
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>
                
                <button
                  onClick={skipBackward}
                  className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-white/10 rounded"
                  title="Voltar 5 segundos"
                >
                  <SkipBack size={18} />
                </button>
                
                <button
                  onClick={skipForward}
                  className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-white/10 rounded"
                  title="Avançar 5 segundos"
                >
                  <SkipForward size={18} />
                </button>

                <button
                  onClick={toggleMute}
                  className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-white/10 rounded"
                  title={isMuted ? 'Ativar som' : 'Desativar som'}
                >
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>

                <div className="text-white text-sm ml-2">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
              </div>

              {/* Controles da direita */}
              <div className="flex items-center gap-2">
                {loop && (
                  <div className="flex items-center gap-1 text-white text-xs bg-white/10 px-2 py-1 rounded">
                    <RotateCcw size={12} />
                    <span>Loop</span>
                  </div>
                )}
                <button
                  onClick={toggleFullscreen}
                  className="text-white hover:text-gray-300 transition-colors p-2 hover:bg-white/10 rounded"
                  title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                >
                  {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPreview;

